
import { Inject, Options, Vue, Watch } from "vue-property-decorator";
import SwipeStepper from "../swipe-stepper/index.vue";
import { NavStep, NavStepStatusID, Section, Step } from "../swipe-stepper/nav-step";
import { AppInsights } from "@/trust-atcs-app/appInsights";
import { TrustAPI } from "@/shared/modules/trust-api";
import { EventEmitter } from "events";
import store from "@/trust-atcs-app/store";
import { RouteN } from "@/trust-atcs-app/constants/router-name";
import LoadingIndicator from "@/shared/components/loading-indicator/index.vue";
import FormPartAConfirmation from "../form-part-a-confirmation/index.vue";
import FormPartADeclaration from "../form-part-a-declaration/index.vue";
import FormPartAOverview from "../form-part-a-overview/index.vue";
import FormPartAPracticeAreas from "../form-part-a-practice-areas/index.vue";
import FormPartATrustAccountDetails from "../form-part-a-trust-account-details/index.vue";
import ConfirmDialogue from '@/shared/components/confirm-dialogue/index.vue';
import { ComplianceForm, PageContent } from "@/shared/modules/typescript-trustapi-client/models";
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import FormHeader from "../form-header/index.vue";
import LoadingSpinner from "@/shared/components/loading-spinner/index.vue";
import { FormPartAData } from "@/trust-atcs-app/models/form-part-a-data";
import FormHeaderTcNote from "../form-header-tc-note/index.vue";
import { IDLESessionManager } from '@/trust-atcs-app/modules/session/idle-session-manager';
import SessionExpired from "../session-expired-modal/index.vue";
import { useFormPartADataStore } from "@/trust-atcs-app/store/useFormPartADataStore";

@Options({
  components: {
    LoadingIndicator,
    SwipeStepper,
    FormHeader,
    FormPartAOverview,
    FormPartAPracticeAreas,
    FormPartATrustAccountDetails,
    FormPartADeclaration,
    FormPartAConfirmation,
    ConfirmDialogue,
    LoadingSpinner,
    FormHeaderTcNote,
    SessionExpired
  },
  mixins: [FormatDateTimeMixin],
  beforeRouteEnter(to: any, from: any, next: any) {
    const complianceFormA = store.getters.getSelectedComplianceFormA;
    if (!complianceFormA) {
      next({ name: RouteN.ATCS.Main.n });
    } else {
      next();
    }
  }
})
export default class FormPartA extends Vue {
  private formPartADataStore = useFormPartADataStore();

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private navSteps = [
    new NavStep(Step.overview, Section.overview), // 0
    new NavStep(Step.practiceAreas, Section.practiceAreas),   // 1
    new NavStep(Step.trustAccountDetails, Section.trustAccountDetails), // 2
    new NavStep(Step.declaration, Section.declaration), // 3
    new NavStep(Step.confirmation, Section.confirmation), // 4
  ];

  private ai = new AppInsights;
  private backButtonText = 'Cancel Application';
  private nextButtonText = 'Next';
  private backButtonVisible = true;

  private formName = {line1: 'Part', line2: 'A'};
  private formDesc = {line1: 'Law Practice Confirmation Statement', line2: ''};
  private isSectionLoading = false;
  private isSaving = false;
  private stopExtending = false;
  private showSessionExpiredModal = false;

  private workerID = -1;

  private sectionName = '';
  private helpContent: PageContent = {};

  private formPartAData: FormPartAData = {
    practiceAreas: {}
  };

  private validSection: boolean[] = [
    true,
    false,
    false,
    false,
    true
  ];

  private idleSessionMgr: IDLESessionManager = new IDLESessionManager(60);

  @Watch('$route')
  public OnRouteChange(val: any, oldVal: any): void {
    const _stored = 'something' //check store data here
    window.scrollTo(0, 0);
    if (oldVal.fullPath.indexOf('confirmation') > -1 || _stored == null) {
      this.$router.push({ name: RouteN.ATCS.DashboardMain.n });
    }
    this.setCurrentStep();
  }

  created(): void {
    // store.dispatch('setFormPartAData', this.formPartAData);
    this.formPartADataStore.setFormPartAData(this.formPartAData);
    this.idleSessionMgr.run(this.idleSessionTimeoutCallback);
    this.workerID = setInterval(() => {
      this.extendSession();
    }, 30000);
  }

  mounted(): void {
    window.scrollTo(0, 0);
    this.eventCtrl.on('save-and-close', this.saveAndClose);
    this.setCurrentStep();
  }

  beforeUnmount(): void {
    clearInterval(this.workerID);
    this.eventCtrl.off('save-and-close', this.saveAndClose);
  }

  beforeRouteLeave(to: any, from: any, next: any): void {
    store.dispatch('setSelectedComplianceFormA', null);
    store.dispatch('setFormUpdatingkey', null);
    // store.dispatch('setFormPartAData', {});
    this.formPartADataStore.clearFormPartAData();
  }

  private exitForm(): void {
    this.stopExtending = true;
    clearInterval(this.workerID);
    this.$router.push({ name: RouteN.ATCS.DashboardHome.n });
    this.showSessionExpiredModal = false;
  }
  private idleSessionTimeoutCallback(): void {
    this.stopExtending = true;
  }

  private setCurrentStep(): void {
    const _section: string = this.$route.query.sec as string;
    if (_section !== Section.overview) {
      this.backButtonText = 'Back';
    } else {
      this.backButtonText = 'Cancel';
    }

    this.nextButtonText = 'Next';
    this.backButtonVisible = true;

    switch (_section) {
      case Section.overview:
        this.backButtonVisible = false;
        this.setSteps(0);
        break;
      case Section.practiceAreas:
        this.setSteps(1);
        break;
      case Section.trustAccountDetails:
        this.setSteps(2);
        break;
      case Section.declaration:
        this.setSteps(3);
        this.nextButtonText = 'Submit';
        break;
      case Section.confirmation:
        this.setSteps(4);
        this.nextButtonText = 'Close';
        this.backButtonVisible = false;
        break;
      default:
        break;
    }
  }

  private setSteps(n: number) {
    this.updateHelpContent(n);
    for (let i = 0; i < this.navSteps.length; i++) {
      this.navSteps[i].status = NavStepStatusID.NotDone
    }
    for (let i = 0; i < n; i++) {
      this.navSteps[i].status = NavStepStatusID.Done
    }
    this.navSteps[n].status = NavStepStatusID.Current
    // the confirmation page
    const _confirmation = this.navSteps.length - 1;
    if (n === _confirmation) {
      for (let i = 0; i < _confirmation; i++) {
        this.navSteps[i].status = NavStepStatusID.Unknown
      }
    } else {
      // save the current step
      // window.sessionStorage.setItem('contractData', JSON.stringify(this.contractData));
    }
  }

  private updateHelpContent(n: number) {
    switch (n) {
      case 0: // Section.overview
        this.helpContent = this.$getPopover('Help', 4);
        break;
      case 1: // Section.practiceAreas
        this.helpContent = this.$getPopover('Help', 5);
        break;
      case 2: // Section.trustAccountDetails
        this.helpContent = this.$getPopover('Help', 6);
        break;
      case 3: // Section.declaration
        this.helpContent = this.$getPopover('Help', 7);
        break;
      case 4: // Section.confirmation
        this.helpContent = this.$getPopover('Help', 8);
        break;
      default:
        this.helpContent = {};
        break;
    }
    this.eventCtrl.emit('updateHelpContent', this.helpContent);
  }

  private goBack() {
    const _section: string = this.$route.query.sec as string;

    this.apiService.PartAFormApi?.apiV2PartAFormExtendFormStatusPost({
      complianceFormId: this.complianceFormA.id,
      updatingKey: this.formUpdatingKey,
    }).then(() => {

      switch (_section) {
        case Section.overview:
          this.eventCtrl.emit('cancel-pc-renew');
          // this.$router.push({ name: RouteN.Mars.PcRenewOverview.n, query: { appId: this.$route.query.appId } });
          break;
        case Section.practiceAreas:
          this.navigate(this.navSteps[0]);
          break;
        case Section.trustAccountDetails:
          this.navigate(this.navSteps[1])
          break;
        case Section.declaration:
          this.navigate(this.navSteps[2]);
          break;
        case Section.confirmation:
          this.eventCtrl.emit('cancel-part-a');
          break;
        default:
          break;
      }

    }).catch((err: any) => {
      this.showSessionExpiredModal = true;
    });
  }

  private navigate(s: NavStep): void {
    const temp: string = JSON.stringify(this.$route.query);
    const tempJSON = JSON.parse(temp);
    tempJSON.sec = s.routeSecName;
    this.$router.push({ query: tempJSON });
  }

  private goNext() {
    // store.dispatch('setPcrFormData', this.pcrFormData);
    const _section = this.$route.query.sec as string;
    switch (_section) {
      case Section.overview:
        // this.saveIndividualDetails(1);
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[1])) : true).catch((err: any) => console.log(err));
        break;
      case Section.practiceAreas:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[2])) : true).catch((err: any) => console.log(err));
        break;
      case Section.trustAccountDetails:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[3])) : true).catch((err: any) => console.log(err));
        break;
      case Section.declaration:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[4])) : true).catch((err: any) => console.log(err));
        break;

      case Section.confirmation:
        // this.openSurveyWindow();
        this.$router.push({ name: RouteN.ATCS.DashboardHome.n });
        break;
      default:
        break;
    }
  }


  private saveAndClose(): void {
    const _confirm: any = this.$refs.confirmSaveClose;
    _confirm.show({
      title: 'Save & Close',
      message: 'By selecting the \'Save & Close\' button, any answered parts will be saved, and you will be taken back to the Homepage. Would you like to proceed?',
      okButton: 'Proceed',
      cancelButton: 'Cancel',
    }).then((res: any) => {
      if (res) {
        this.saveSection(true, true);
      }
    })
  }

  private async extendSession(): Promise<void> {
    try {
      if (this.stopExtending) {
        clearInterval(this.workerID);
        this.showSessionExpiredModal = true;
        return;
      }
      if (this.isSaving) {
        return;
      }
      await this.apiService.PartAFormApi?.apiV2PartAFormExtendFormStatusPost({
        complianceFormId: this.complianceFormA.id,
        updatingKey:this.formUpdatingKey
      });

    } catch (err) {
      clearInterval(this.workerID);
      this.showSessionExpiredModal = true;
    }
  }

  private async saveSection(close?: boolean, skip?: boolean): Promise<boolean> {
    try {
      this.isSaving = true;

      const _section = this.$route.query.sec as string;

      await this.apiService.PartAFormApi?.apiV2PartAFormExtendFormStatusPost({
        complianceFormId: this.complianceFormA.id,
        updatingKey: this.formUpdatingKey,
      });

      switch (_section) {
        case Section.practiceAreas:
          await this.apiService.PartAFormApi?.apiV2PartAFormSavePracticeAreasPost({
            id: this.formPartAData?.practiceAreas?.id,
            complianceFormStatus: this.formPartAData?.practiceAreas?.complianceFormStatus,
            atcsMainQuestions: this.formPartAData?.practiceAreas?.atcsMainQuestions,
            complianceFormId: this.complianceFormA.id,
            updatingKey:this.formUpdatingKey,
            skipValidation: skip,
          });

          if (skip && !close)
            this.eventCtrl.emit('reload-form-a-practicearea-content');
          break;
        case Section.trustAccountDetails:
          await this.apiService.PartAFormApi?.apiV2PartAFormSaveTrustAccountDetailsPost({
            id: this.formPartAData?.trustAccountDetails?.id,
            complianceFormStatus: this.formPartAData?.trustAccountDetails?.complianceFormStatus,
            atcsMainQuestions: this.formPartAData?.trustAccountDetails?.atcsMainQuestions,
            complianceFormId: this.complianceFormA.id,
            updatingKey:this.formUpdatingKey,
            skipValidation: skip,
          });
          if (skip && !close)
            this.eventCtrl.emit('reload-form-a-tadetails-content');
          break;
        case Section.declaration:
          if (!close) {
            await this.apiService.PartAFormApi?.apiV2PartAFormSubmitFormPost({
              complianceFormId: this.complianceFormA.id,
              updatingKey: this.formUpdatingKey
            });
          }
          break;
        case Section.confirmation:
          break;
        default:
          break;
      }

      if (close) {
        await this.apiService.PartAFormApi?.apiV2PartAFormClosePost({
          complianceFormId: this.complianceFormA.id,
          updatingKey: this.formUpdatingKey ?? null
        });
        this.$router.push({ name: RouteN.ATCS.DashboardHome.n });
      }
      return Promise.resolve(false);
    } catch (err) {
      // this.showSessionExpiredModal = true;
      this.eventCtrl.emit('show-snackbar', 'error', 'Error saving section');
      return Promise.reject(true);
    } finally {
      this.isSaving = false;
    }
  }

  private validationForm(i: any, isValid: any) {
    this.validSection[i] = isValid;
    this.canGoNext();
  }

  private canGoNext(): boolean {
    // check if the current section is valid
    const _section = this.$route.query.sec as string;

    switch (_section) {
      case Section.overview:
        return true;
      case Section.practiceAreas:
        return this.validSection[1];
      case Section.trustAccountDetails:
        return this.validSection[2];
      case Section.declaration:
        return this.validSection[3];
      case Section.confirmation:
        return true;
      default:
        return false;
    }
  }

  get $Section() {
    return Section;
  }

  get currentSection(): string {
    return this.$route.query.sec as string;
  }

  get savingText() {
    return 'Saving...';
  }

  get complianceFormA(): ComplianceForm {
    return store.getters.getSelectedComplianceFormA;
  }

  get formUpdatingKey(): string {
    return store.getters.getFormUpdatingKey;
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private setFormDescription(message: any) {
    if (message) {
      this.formDesc = { line1: 'Confirmation of Trust Money (Part A) as at ' + message, line2: '' };
    } 
  }

}

